import { useState, useEffect, useContext, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import SoftTypography from "components/SoftTypography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Calendar from "cxfcomponents/Calendar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import wallet from "assets/images/flowDesigner/wallet.png";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddIcon from "@mui/icons-material/Add";

import SchedulerDialog from "./components/SchedulerDialog";
import DeleteScheduleMenuItem from "./components/DeleteSchedulteMenuItem";
import { fetchCalendarEvents, pauseSchedule, resumeSchedule } from "redux/actions/flowDesigner/calendarActionCreator";
import { getLocalDateTimeString } from "helpers/common";
import ProtectedButton from "authentication/ProtectedButton";

import { PermissionsContext } from "context/AppProvider/PermissionsContext";

import { useUserRole } from "hooks/useUserRole";
import { PERMISSIONS } from "constants/portal";

const anchorOrigin = { vertical: "bottom", horizontal: "right" };
const transformOrigin = { vertical: "top", horizontal: "center" };

function ExecutionsCalendar() {
   const { userPermissions } = useContext(PermissionsContext);
   const { calendarEvents } = useSelector((store) => store?.designer?.calendar);
   const dispatch = useDispatch();
   const { user } = useAuth0();
   const { currentUserRole } = useUserRole();
   const { t } = useTranslation("flowDesigner");
   const [upcomingEvents, setUpcomingEvents] = useState([]);
   const [anchorEl, setAnchorEl] = useState(null);
   const [selectedDate, setSelectedDate] = useState(null);
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [openSchedulerDialog, setOpenSchedulerDialog] = useState(false);
   const [combinedEvents, setCombinedEvents] = useState([]);

   const checkAllowAddSchedule = () => {
      if (userPermissions?.length > 0) {
         return userPermissions?.find((permission) => permission.name === PERMISSIONS.ADD_SCHEDULES)?.value;
      }
      return false;
   };

   const handleOpenSchedulerDialog = (e, isCurrentDate) => {
      if (!checkAllowAddSchedule()) {
         return;
      }
      setSelectedEvent(null);

      if (e?.date && !isCurrentDate) {
         setSelectedDate(e.date);
      }

      if (isCurrentDate) {
         setSelectedDate(undefined);
      }
      setOpenSchedulerDialog(true);
   };

   const handleCloseSchedulerDialog = () => {
      setOpenSchedulerDialog(false);
      setSelectedEvent(null);
   };

   const handleMenu = (e, ev) => {
      const event = { ...ev };

      if (event.savedEndDate) {
         event.end = event.savedEndDate;
      }

      if (event.savedStartDate) {
         event.start = event.savedStartDate;
      }

      event.extendedProps = {
         description: event.description,
         flowId: event.flowId,
         repeat: event.repeat,
         repetition: event.repetition,
         status: event.status
      };

      setAnchorEl(e.currentTarget);
      setSelectedEvent(event);
   };

   const handleCloseMenu = () => {
      setAnchorEl(null);
   };

   const handleEventClick = (e) => {
      const eventJSON = JSON.parse(JSON.stringify(e.event.toJSON()));

      if (eventJSON.extendedProps.savedEndDate) {
         eventJSON.end = eventJSON.extendedProps.savedEndDate;
      }

      if (eventJSON.extendedProps.savedStartDate) {
         eventJSON.start = eventJSON.extendedProps.savedStartDate;
      }

      if (eventJSON.extendedProps?.eventId) {
         eventJSON.id = eventJSON.extendedProps.eventId;
      }

      setAnchorEl(e.el.parentNode.parentNode);
      setSelectedEvent(eventJSON);
   };

   const editEvent = () => {
      handleCloseMenu();
      setOpenSchedulerDialog(true);
   };

   const pauseEvent = () => {
      handleCloseMenu();
      dispatch(pauseSchedule(user?.stripeId, selectedEvent?.id, selectedEvent?.extendedProps?.flowId));
   };

   const resumeEvent = () => {
      const status = selectedEvent?.extendedProps?.repeat === "not_repeat" || selectedEvent?.repeat === "not_repeat" ? "Active" : "Recurring";
      handleCloseMenu();
      dispatch(resumeSchedule(user?.stripeId, selectedEvent?.id, selectedEvent?.extendedProps?.flowId, status));
   };

   const fetchCalendar = () => {
      dispatch(fetchCalendarEvents(user?.stripeId));
   };

   const filterCalendarEvents = useMemo(() => {
      if (currentUserRole?.name?.toLowerCase() === "owner" || userPermissions.find((p) => p.name === PERMISSIONS.VIEW_ANY_SCHEDULES)?.value) {
         return calendarEvents;
      } else {
         return calendarEvents.filter((event) => event.created_by_id === user?.cxfUserId);
      }
   }, [calendarEvents, currentUserRole]);

   const isAllowedAction = (permissions) => {
      return permissions.some((permission) => userPermissions.find((userPermission) => userPermission.name === permission)?.value);
   };

   const isOwner = useMemo(() => {
      return selectedEvent?.extendedProps?.created_by_id === user?.cxfUserId;
   });

   useEffect(() => {
      if (calendarEvents.length) return;

      fetchCalendar();
   }, []);

   useEffect(() => {
      const upEvents = [...combinedEvents];
      upEvents.sort((a, b) => {
         return new Date(a.start) - new Date(b.start);
      });
      const filteredUpEvents = upEvents?.filter((e) => new Date(e.start) > new Date()).slice(0, 10);
      setUpcomingEvents(filteredUpEvents);
   }, [combinedEvents]);

   const recuringEventMenuItem = () => {
      return selectedEvent?.extendedProps?.status === "Paused" || selectedEvent?.status === "Paused" ? <MenuItem onClick={resumeEvent}>Enable</MenuItem> : <MenuItem onClick={pauseEvent}>Disable</MenuItem>;
   };

   return (
      <>
         <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 8 }}>
               <Calendar
                  initialView="dayGridMonth"
                  events={filterCalendarEvents}
                  dateClick={(e) => handleOpenSchedulerDialog(e)}
                  eventClick={handleEventClick}
                  height="100%"
                  selectable={false}
                  editable={false}
                  setCombinedEvents={setCombinedEvents}
                  combinedEvents={combinedEvents}
               />
            </Grid>
            <Grid size={{ xs: 4 }}>
               <SoftBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <SoftTypography variant="h6" component="div">
                     {t("common.upcoming_events")}
                  </SoftTypography>
                  <ProtectedButton permissionName={PERMISSIONS.ADD_SCHEDULES}>
                     <SoftButton color="primary" onClick={(e) => handleOpenSchedulerDialog(e, true)} sx={{ padding: "8px 24px" }}>
                        <AddIcon />
                        &nbsp;
                        {t("buttons.add_new_schedule")}
                     </SoftButton>
                  </ProtectedButton>
               </SoftBox>
               <List className="upcoming-events-list">
                  {upcomingEvents?.map((event) => {
                     return (
                        <ListItem sx={{ mb: 2 }} key={event.id} disablePadding>
                           <ListItemAvatar>
                              <Avatar>
                                 <img src={wallet} alt="flow schedule icon" />
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText primary={event.title} secondary={getLocalDateTimeString(event.start)} />
                           <IconButton size="small" onClick={(e) => handleMenu(e, event)}>
                              <MoreHorizIcon />
                           </IconButton>
                        </ListItem>
                     );
                  })}
               </List>
            </Grid>
         </Grid>
         <Menu className="groups-submenu" anchorEl={anchorEl} anchorOrigin={anchorOrigin} keepMounted transformOrigin={transformOrigin} open={!!anchorEl} onClose={handleCloseMenu}>
            {(isAllowedAction([PERMISSIONS.EDIT_SCHEDULES]) && isOwner) || isAllowedAction([PERMISSIONS.EDIT_ANY_SCHEDULES]) ? <MenuItem onClick={editEvent}>{t("menus.edit")}</MenuItem> : null}
            {/* {selectedEvent?.extendedProps?.repeat !== "not_repeat" && selectedEvent?.repeat !== "not_repeat" && recuringEventMenuItem()} */}
            <ProtectedButton permissionName={PERMISSIONS.PAUSE_SCHEDULES}>{isOwner && recuringEventMenuItem()}</ProtectedButton>
            {(isAllowedAction([PERMISSIONS.DELETE_SCHEDULES]) && isOwner) || isAllowedAction([PERMISSIONS.DELETE_ANY_SCHEDULES]) ? (
               <DeleteScheduleMenuItem handleClose={handleCloseMenu} selectedEvent={selectedEvent} isMenuItem />
            ) : null}
         </Menu>
         <SchedulerDialog open={openSchedulerDialog} selectedEvent={selectedEvent} selectedDate={selectedDate} closeDialog={handleCloseSchedulerDialog} />
      </>
   );
}

export default ExecutionsCalendar;
